import {
	Fragment,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Select from 'react-select';
import { FaCheck, FaEye, FaRedo, FaRegCopy, FaRegEdit } from 'react-icons/fa';
import {
	Badge,
	Button,
	Card,
	Container,
	Form,
	Modal,
	Row,
	Spinner,
} from 'react-bootstrap';
import ServerPaginatedTable from 'src/components/ServerPaginatedTable/ServerPaginatedTable';
import { FileExportMenu } from 'src/components/FileExportMenu/FileExportMenu';
import type { ExportFormat } from 'src/components/FileExportMenu/FileExportMenu';
import toaster from 'src/utils/toaster';
import { downloadCSV, downloadXLSX } from 'src/utils';
import { useApiClient } from 'src/hooks/useApiClient';
import { useVehicleAutosuggest } from '../common/useVehicleAutosuggest';
import MotornetVehicleSelector, {
	MakeOption,
	ModelOption,
	VersionOption as ManualVersionOption,
} from '../common/MotornetVehicleSelector';
import { useMotornetVehicle } from '../common/useMotornetVehicle';
import type {
	EurotaxBackfillFilter,
	EurotaxBackfillResolveRequest,
	EurotaxBackfillVehicle,
	EurotaxBackfillVehiclesResponse,
	EurotaxBackfillRetryOutcome,
	MotornetAutosuggestMatch,
	MotornetVehicleResponse,
} from '../types';
import { buildExportData, exportTimestamp } from './exportUtils';
import type { MigrationDebug } from './vehicleStatus';
import {
	VEHICLE_STATUS_META,
	isResolvable,
	isRetryable,
	migrationDebugOf,
	statusKeyOf,
} from './vehicleStatus';
import './ResolveVehiclePage.scss';

function StatusBadge({ vehicle }: { vehicle: EurotaxBackfillVehicle }) {
	const { t } = useTranslation();
	const status = VEHICLE_STATUS_META[statusKeyOf(vehicle)];

	return (
		<Badge bg={status.bg} text={status.text} pill>
			{t(status.labelKey, status.fallback)}
		</Badge>
	);
}

const PAGE_SIZE = 20;
const EXPORT_BATCH_SIZE = 100;
const COPIED_FEEDBACK_MS = 1000;

const FILTER_OPTIONS: {
	value: EurotaxBackfillFilter;
	labelKey: string;
	fallback: string;
}[] = [
		{
			value: 'resolved',
			labelKey: 'motornet_backfill_filter_resolved',
			fallback: 'Risolto',
		},
		{
			value: 'ambiguous',
			labelKey: 'motornet_backfill_filter_ambiguous',
			fallback: 'Ambiguo',
		},
		{
			value: 'notFound',
			labelKey: 'motornet_backfill_filter_not_found',
			fallback: 'Non trovato',
		},
		{
			value: 'priceMismatch',
			labelKey: 'motornet_backfill_filter_price_mismatch',
			fallback: 'Prezzo divergente',
		},
		{
			value: 'skipped',
			labelKey: 'motornet_backfill_filter_skipped',
			fallback: 'Saltato',
		},
		{
			value: 'manual',
			labelKey: 'motornet_backfill_filter_manual',
			fallback: 'Manuale',
		},
		{
			value: 'neverProcessed',
			labelKey: 'motornet_backfill_filter_never_processed',
			fallback: 'Mai processato',
		},
	];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_FILTER: EurotaxBackfillFilter = 'ambiguous';
const ALL_FILTER_PARAM = 'all';

function parseFilter(raw: string | null): EurotaxBackfillFilter | '' {
	if (raw === ALL_FILTER_PARAM) return '';
	return FILTER_OPTIONS.some((option) => option.value === raw)
		? (raw as EurotaxBackfillFilter)
		: DEFAULT_FILTER;
}

function serializeFilter(filter: EurotaxBackfillFilter | ''): string {
	return filter === '' ? ALL_FILTER_PARAM : filter;
}

function parsePage(raw: string | null): number {
	const page = Number(raw);
	return Number.isInteger(page) && page >= 1 ? page : 1;
}

function parsePageSize(raw: string | null): number {
	const size = Number(raw);
	return PAGE_SIZE_OPTIONS.includes(size) ? size : PAGE_SIZE;
}

function useBackfillVehicles(
	page: number,
	pageSize: number,
	filter: EurotaxBackfillFilter | '',
	manufactureCode: string
) {
	const { apiClient } = useApiClient();

	return useQuery<EurotaxBackfillVehiclesResponse>({
		queryKey: [
			'motornet',
			'eurotax-backfill',
			page,
			pageSize,
			filter,
			manufactureCode,
		],
		queryFn: () =>
			apiClient.motornetControllerService.getEurotaxBackfillVehicles({
				page,
				pageSize,
				filter: filter || undefined,
				manufactureCode: manufactureCode || undefined,
			}),
		staleTime: 0,
		gcTime: 0,
		retry: false,
		refetchOnWindowFocus: false,
	});
}

interface CopyIdButtonProps {
	value: string;
	label: string;
}

function CopyIdButton({ value, label }: CopyIdButtonProps) {
	const { t } = useTranslation();
	const [copied, setCopied] = useState(false);
	const resetTimeout = useRef<ReturnType<typeof setTimeout>>();

	useEffect(() => () => clearTimeout(resetTimeout.current), []);

	const copyLabel = `${t('motornet_backfill_copy', 'Copia')} ${label}`;
	const copiedLabel = t('motornet_backfill_copied', '{{label}} copiato', {
		label,
	});

	const handleCopy = async (event: MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		try {
			await navigator.clipboard.writeText(value);
			setCopied(true);
			clearTimeout(resetTimeout.current);
			resetTimeout.current = setTimeout(
				() => setCopied(false),
				COPIED_FEEDBACK_MS
			);
		} catch {
			toaster.error(t('motornet_import_copy_failed', 'Copia non riuscita'));
		}
	};

	return (
		<Button
			variant="link"
			className={`p-0 ms-2 lh-1 ${copied ? 'text-success' : 'text-muted'}`}
			aria-label={copied ? copiedLabel : copyLabel}
			title={copied ? copiedLabel : copyLabel}
			onClick={handleCopy}
		>
			{copied ? <FaCheck /> : <FaRegCopy />}
		</Button>
	);
}

interface DebugSummaryProps {
	debug: MigrationDebug;
	defaultOpen?: boolean;
}

function DebugSummary({ debug, defaultOpen = false }: DebugSummaryProps) {
	const { t } = useTranslation();

	return (
		<details className="text-muted small mt-3" open={defaultOpen}>
			<summary className="font-weight-bold" style={{ cursor: 'pointer' }}>
				{t('motornet_backfill_debug', 'Migration debug')}
			</summary>
			<dl className="debug-grid ps-2">
				<dt>{t('motornet_backfill_debug_raw_code', 'Codice raw')}</dt>
				<dd className="font-monospace">
					{debug.codice_costruttore_raw ?? '—'}
				</dd>
				<dt>{t('motornet_backfill_debug_code_used', 'Codice usato')}</dt>
				<dd className="font-monospace">{debug.manufacture_code_used ?? '—'}</dd>
				<dt>{t('motornet_backfill_debug_outcome', 'Esito')}</dt>
				<dd>{debug.outcome ?? '—'}</dd>
				<dt>{t('motornet_backfill_debug_retries', 'Tentativi')}</dt>
				<dd>{debug.retry_count ?? '—'}</dd>
				<dt>{t('motornet_backfill_debug_kinds', 'Tipologie provate')}</dt>
				<dd>{debug.vehicle_kinds_tried?.join(', ') ?? '—'}</dd>
				{Object.entries(debug.errors_by_kind ?? {}).map(([kind, message]) => (
					<Fragment key={kind}>
						<dt>
							{t('motornet_backfill_debug_error', 'Errore')} ({kind})
						</dt>
						<dd>{message}</dd>
					</Fragment>
				))}
				{debug.message && (
					<>
						<dt>{t('motornet_backfill_debug_message', 'Messaggio')}</dt>
						<dd>
							{debug.message.split(' | ').map((part, index) => (
								<div key={index} className="message-line">
									{part}
								</div>
							))}
						</dd>
					</>
				)}
				<dt>{t('motornet_backfill_debug_processed_at', 'Processato il')}</dt>
				<dd>
					{debug.processed_at
						? new Date(debug.processed_at).toLocaleString('it-IT')
						: '—'}
				</dd>
			</dl>
		</details>
	);
}

interface ResolveVehicleModalProps {
	vehicle: EurotaxBackfillVehicle | null;
	onClose: () => void;
}

interface VersionOption {
	value: string;
	label: string;
	match: MotornetAutosuggestMatch;
}

const versionOptionKey = (match: MotornetAutosuggestMatch) =>
	`${match.vehicleKind}:${match.codiceEurotax}`;

const versionOptionLabel = (match: MotornetAutosuggestMatch) =>
	[
		match.marca.nome,
		match.descrizioneModelloBreveCarrozzeria,
		match.versione,
		match.codiceCostruttore,
	]
		.filter(Boolean)
		.join(' · ');

function buildManualResolveBody(
	mongoIds: string[],
	brand: MakeOption,
	model: ModelOption,
	version: ManualVersionOption,
	fullVehicle: MotornetVehicleResponse | undefined
): EurotaxBackfillResolveRequest | null {
	const vehicleCode = version.data.codiceEurotax || version.value;
	const manufactureCode =
		fullVehicle?.details?.modello?.codiceCostruttore ??
		version.data.codiceCostruttore;
	if (!vehicleCode || !manufactureCode || mongoIds.length === 0) return null;
	return {
		mongoIds,
		vehicle_code: vehicleCode,
		vehicle_kind: brand.data.vehicleKind,
		manufacture_code: manufactureCode,
		make_code: brand.data.acronimo,
		model_code: model.value,
	};
}

function ResolveVehicleModal({ vehicle, onClose }: ResolveVehicleModalProps) {
	const { t } = useTranslation();
	const { apiClient } = useApiClient();
	const queryClient = useQueryClient();
	const debug = vehicle ? migrationDebugOf(vehicle) : null;
	const isAmbiguous = !!vehicle && vehicle.ambiguous;
	const isNotFound = !!vehicle && vehicle.not_found && !vehicle.ambiguous;
	const isPriceMismatch = !!vehicle && statusKeyOf(vehicle) === 'priceMismatch';
	const showResolutionForm = !!vehicle && isResolvable(vehicle);
	const manufactureCode =
		vehicle?.manufacture_code ?? vehicle?.codice_costruttore ?? '';

	const {
		data: autosuggest,
		isFetching: isLoadingMatches,
		isError: matchesFailed,
	} = useVehicleAutosuggest(manufactureCode, {
		enabled: isAmbiguous,
		staleTime: 0,
		gcTime: 0,
	});

	const [selectedKey, setSelectedKey] = useState<string | null>(null);
	const versionOptions = useMemo<VersionOption[]>(
		() =>
			(autosuggest?.matches ?? []).map((match) => ({
				value: versionOptionKey(match),
				label: versionOptionLabel(match),
				match,
			})),
		[autosuggest]
	);
	const selectedVersion =
		versionOptions.find((option) => option.value === selectedKey) ?? null;

	const [manualBrand, setManualBrand] = useState<MakeOption | null>(null);
	const [manualModel, setManualModel] = useState<ModelOption | null>(null);
	const [manualVersion, setManualVersion] =
		useState<ManualVersionOption | null>(null);
	const [priceOverride, setPriceOverride] = useState(false);

	useEffect(() => {
		setSelectedKey(null);
		setManualBrand(null);
		setManualModel(null);
		setManualVersion(null);
		setPriceOverride(vehicle?.price_manual_override ?? false);
	}, [vehicle?.platform_uuid, vehicle?.price_manual_override]);

	const {
		data: manualVehicle,
		isFetching: isHydratingVersion,
		isError: hydrationFailed,
		error: hydrationError,
	} = useMotornetVehicle(
		manualVersion?.value ?? null,
		manualBrand?.data.vehicleKind
	);

	useEffect(() => {
		if (!isNotFound || !hydrationFailed) return;
		toaster.error(
			hydrationError instanceof Error && hydrationError.message
				? hydrationError.message
				: t(
					'motornet_backfill_vehicle_load_error',
					'Impossibile recuperare i dati del veicolo'
				)
		);
	}, [isNotFound, hydrationFailed, hydrationError, t]);

	const ambiguousBody: EurotaxBackfillResolveRequest | null =
		vehicle && selectedVersion
			? {
				mongoIds: [vehicle.platform_uuid],
				vehicle_code: selectedVersion.match.codiceEurotax,
				vehicle_kind: selectedVersion.match.vehicleKind,
				manufacture_code: selectedVersion.match.codiceCostruttore,
				make_code: selectedVersion.match.marca.acronimo,
				model_code: selectedVersion.match.codiceModello,
			}
			: null;

	const notFoundBody: EurotaxBackfillResolveRequest | null =
		vehicle && manualBrand && manualModel && manualVersion
			? buildManualResolveBody(
				[vehicle.platform_uuid],
				manualBrand,
				manualModel,
				manualVersion,
				manualVehicle
			)
			: null;

	const resolveBody = isAmbiguous
		? ambiguousBody
		: isNotFound
			? notFoundBody
			: null;

	const resolveMutation = useMutation({
		mutationFn: (body: EurotaxBackfillResolveRequest) =>
			apiClient.motornetControllerService.resolveEurotaxBackfill(body),
		onSuccess: (result) => {
			if (result.updated.length === 0) {
				toaster.error(
					t('motornet_backfill_resolve_not_found', 'Nessun veicolo aggiornato')
				);
				return;
			}
			toaster.success(
				t('motornet_backfill_resolve_success', 'Veicolo risolto')
			);
			void queryClient.invalidateQueries({
				queryKey: ['motornet', 'eurotax-backfill'],
			});
			onClose();
		},
		onError: (error) => {
			toaster.error(
				error instanceof Error && error.message
					? error.message
					: t('motornet_backfill_resolve_error', 'Risoluzione non riuscita')
			);
		},
	});
	const priceOverrideMutation = useMutation({
		mutationFn: (body: {
			mongoId: string;
			priceManualOverride: boolean;
		}) =>
			apiClient.motornetControllerService.setEurotaxBackfillPriceOverride(
				body.mongoId,
				{ priceManualOverride: body.priceManualOverride }
			),
		onSuccess: () => {
			toaster.success(
				t('motornet_backfill_price_override_success', 'Prezzo aggiornato')
			);
			void queryClient.invalidateQueries({
				queryKey: ['motornet', 'eurotax-backfill'],
			});
			onClose();
		},
		onError: (error) => {
			toaster.error(
				error instanceof Error && error.message
					? error.message
					: t('motornet_backfill_resolve_error', 'Risoluzione non riuscita')
			);
		},
	});

	const isSaving = resolveMutation.isPending || priceOverrideMutation.isPending;

	const handleSave = () => {
		if (isSaving) return;
		if (isPriceMismatch) {
			if (!vehicle) return;
			priceOverrideMutation.mutate({
				mongoId: vehicle.platform_uuid,
				priceManualOverride: priceOverride,
			});
			return;
		}
		if (!resolveBody) return;
		resolveMutation.mutate(resolveBody);
	};

	return (
		<Modal
			show={!!vehicle}
			onHide={() => !isSaving && onClose()}
			size="lg"
			centered
			backdrop="static"
			keyboard={false}
			contentClassName="resolve-vehicle-modal"
		>
			{vehicle && (
				<>
					<Modal.Header closeButton={!isSaving}>
						<Modal.Title>
							{vehicle.marca} {vehicle.modello}
						</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<div className="text-muted">{vehicle.allestimento}</div>
						<div className="font-monospace text-muted small d-flex align-items-center">
							<Link
								to={`/details/${vehicle.platform_uuid}`}
								target="_blank"
								rel="noopener noreferrer"
							>
								{vehicle.platform_uuid}
							</Link>
							<CopyIdButton
								value={vehicle.platform_uuid}
								label={t('motornet_backfill_platform_id', 'Platform id')}
							/>
						</div>
						<div className="font-monospace text-muted small d-flex align-items-center">
							{vehicle.codice_costruttore}
							{vehicle.codice_costruttore && (
								<CopyIdButton
									value={vehicle.codice_costruttore}
									label={t(
										'motornet_backfill_manufacturer_code',
										'Codice costruttore'
									)}
								/>
							)}
						</div>
						{vehicle.jato_id != null && (
							<div className="font-monospace text-muted small">
								{t('motornet_backfill_jato_id', 'JATO id')}: {vehicle.jato_id}
							</div>
						)}
						{debug && (
							<DebugSummary debug={debug} defaultOpen={!showResolutionForm} />
						)}
						{isAmbiguous && (
							<div className="mt-4">
								{isLoadingMatches ? (
									<div className="text-center text-muted py-4">
										<Spinner animation="border" />
									</div>
								) : (
									<>
										<div className="font-weight-bold mb-2">
											{t(
												'motornet_backfill_pick_version',
												'Seleziona versione'
											)}
										</div>
										<Select<VersionOption>
											options={versionOptions}
											value={selectedVersion}
											isClearable
											isDisabled={isSaving}
											placeholder={t(
												'motornet_import_pick_version',
												'seleziona versione…'
											)}
											menuPortalTarget={document.body}
											styles={{
												menuPortal: (base) => ({ ...base, zIndex: 1090 }),
											}}
											noOptionsMessage={() =>
												matchesFailed
													? t(
														'motornet_backfill_matches_error',
														'Errore nel recupero delle versioni'
													)
													: t(
														'eurotax_vehicleSelect_noResults',
														'Nessun risultato'
													)
											}
											onChange={(option) =>
												setSelectedKey(option?.value ?? null)
											}
										/>
									</>
								)}
							</div>
						)}
						{isPriceMismatch && (
							<div className="mt-4">
								<div className="font-weight-bold mb-2">
									{t(
										'motornet_backfill_price_override',
										'Override manuale del prezzo'
									)}
								</div>
								<Form.Check
									type="radio"
									id="price-override-true"
									name="price-override"
									label={t('yes', 'Sì')}
									checked={priceOverride}
									disabled={isSaving}
									onChange={() => setPriceOverride(true)}
								/>
								<Form.Check
									type="radio"
									id="price-override-false"
									name="price-override"
									label={t('no', 'No')}
									checked={!priceOverride}
									disabled={isSaving}
									onChange={() => setPriceOverride(false)}
								/>
							</div>
						)}
						{isNotFound && (
							<div className="mt-4">
								<div className="font-weight-bold mb-2">
									{t('motornet_backfill_pick_version', 'Seleziona versione')}
								</div>
								<Row>
									<MotornetVehicleSelector
										selectedVehicle={{
											brand: manualBrand,
											model: manualModel,
											version: manualVersion,
										}}
										onBrandChange={setManualBrand}
										onModelChange={setManualModel}
										onVersionChange={setManualVersion}
										showSelectLabels
										requiredBrand
										requiredModel
										requiredVersion
										disabled={isSaving}
										vertical
									/>
								</Row>
							</div>
						)}
					</Modal.Body>
					{showResolutionForm && (
						<Modal.Footer>
							<Button
								onClick={handleSave}
								disabled={
									isSaving ||
									(!isPriceMismatch && !resolveBody) ||
									(isAmbiguous && isLoadingMatches)
								}
							>
								{(isSaving || (isNotFound && isHydratingVersion)) && (
									<Spinner animation="border" size="sm" className="me-2" />
								)}
								{t('motornet_import_save', 'Salva')}
							</Button>
						</Modal.Footer>
					)}
				</>
			)}
		</Modal>
	);
}

interface SelectAllCheckboxProps {
	checked: boolean;
	indeterminate: boolean;
	onChange: () => void;
	ariaLabel: string;
}

function SelectAllCheckbox({
	checked,
	indeterminate,
	onChange,
	ariaLabel,
}: SelectAllCheckboxProps) {
	const ref = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (ref.current) {
			ref.current.indeterminate = !checked && indeterminate;
		}
	}, [checked, indeterminate]);

	return (
		<input
			ref={ref}
			type="checkbox"
			className="form-check-input"
			checked={checked}
			onChange={onChange}
			onClick={(event) => event.stopPropagation()}
			aria-label={ariaLabel}
		/>
	);
}

interface BulkResolveModalProps {
	open: boolean;
	mode: 'ambiguous' | 'notFound';
	manufactureCode: string;
	mongoIds: string[];
	onClose: () => void;
	onResolved: () => void;
}

function BulkResolveModal({
	open,
	mode,
	manufactureCode,
	mongoIds,
	onClose,
	onResolved,
}: BulkResolveModalProps) {
	const { t } = useTranslation();
	const { apiClient } = useApiClient();
	const queryClient = useQueryClient();
	const isAmbiguous = mode === 'ambiguous';
	const isNotFound = mode === 'notFound';

	const {
		data: autosuggest,
		isFetching: isLoadingMatches,
		isError: matchesFailed,
	} = useVehicleAutosuggest(manufactureCode, {
		enabled: open && isAmbiguous && !!manufactureCode,
		staleTime: 0,
		gcTime: 0,
	});

	const [selectedKey, setSelectedKey] = useState<string | null>(null);
	const [manualBrand, setManualBrand] = useState<MakeOption | null>(null);
	const [manualModel, setManualModel] = useState<ModelOption | null>(null);
	const [manualVersion, setManualVersion] =
		useState<ManualVersionOption | null>(null);

	useEffect(() => {
		setSelectedKey(null);
		setManualBrand(null);
		setManualModel(null);
		setManualVersion(null);
	}, [open, mode, manufactureCode]);

	const versionOptions = useMemo<VersionOption[]>(
		() =>
			(autosuggest?.matches ?? []).map((match) => ({
				value: versionOptionKey(match),
				label: versionOptionLabel(match),
				match,
			})),
		[autosuggest]
	);
	const selectedVersion =
		versionOptions.find((option) => option.value === selectedKey) ?? null;

	const {
		data: manualVehicle,
		isFetching: isHydratingVersion,
		isError: hydrationFailed,
		error: hydrationError,
	} = useMotornetVehicle(
		manualVersion?.value ?? null,
		manualBrand?.data.vehicleKind
	);

	useEffect(() => {
		if (!isNotFound || !hydrationFailed) return;
		toaster.error(
			hydrationError instanceof Error && hydrationError.message
				? hydrationError.message
				: t(
					'motornet_backfill_vehicle_load_error',
					'Impossibile recuperare i dati del veicolo'
				)
		);
	}, [isNotFound, hydrationFailed, hydrationError, t]);

	const ambiguousBody: EurotaxBackfillResolveRequest | null = selectedVersion
		? {
			mongoIds,
			vehicle_code: selectedVersion.match.codiceEurotax,
			vehicle_kind: selectedVersion.match.vehicleKind,
			manufacture_code: selectedVersion.match.codiceCostruttore,
			make_code: selectedVersion.match.marca.acronimo,
			model_code: selectedVersion.match.codiceModello,
		}
		: null;

	const notFoundBody: EurotaxBackfillResolveRequest | null =
		manualBrand && manualModel && manualVersion
			? buildManualResolveBody(
				mongoIds,
				manualBrand,
				manualModel,
				manualVersion,
				manualVehicle
			)
			: null;

	const resolveBody = isAmbiguous ? ambiguousBody : notFoundBody;

	const resolveMutation = useMutation({
		mutationFn: (body: EurotaxBackfillResolveRequest) =>
			apiClient.motornetControllerService.resolveEurotaxBackfill(body),
		onSuccess: (result) => {
			if (result.updated.length === 0) {
				toaster.error(
					t('motornet_backfill_resolve_not_found', 'Nessun veicolo aggiornato')
				);
				return;
			}
			const message = t(
				'motornet_backfill_bulk_success',
				'{{count}} veicoli aggiornati',
				{ count: result.updated.length }
			);
			toaster.success(
				result.notFound.length > 0
					? `${message} · ${t(
						'motornet_backfill_bulk_not_found',
						'{{count}} non aggiornati',
						{ count: result.notFound.length }
					)}`
					: message
			);
			void queryClient.invalidateQueries({
				queryKey: ['motornet', 'eurotax-backfill'],
			});
			onResolved();
			onClose();
		},
		onError: (error) => {
			toaster.error(
				error instanceof Error && error.message
					? error.message
					: t('motornet_backfill_resolve_error', 'Risoluzione non riuscita')
			);
		},
	});
	const isSaving = resolveMutation.isPending;

	const handleSave = () => {
		if (!resolveBody || mongoIds.length === 0 || isSaving) return;
		resolveMutation.mutate(resolveBody);
	};

	return (
		<Modal
			show={open}
			onHide={() => !isSaving && onClose()}
			size="lg"
			centered
			backdrop="static"
			keyboard={false}
			contentClassName="resolve-vehicle-modal"
		>
			<Modal.Header closeButton={!isSaving}>
				<Modal.Title>
					{t('motornet_backfill_bulk_title', 'Update multiplo')}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className="text-muted">
					{t('motornet_backfill_bulk_count', '{{count}} veicoli selezionati', {
						count: mongoIds.length,
					})}
				</div>
				<div className="font-monospace text-muted small mb-3">
					{manufactureCode}
				</div>
				{isAmbiguous &&
					(isLoadingMatches ? (
						<div className="text-center text-muted py-4">
							<Spinner animation="border" />
						</div>
					) : (
						<>
							<div className="font-weight-bold mb-2">
								{t('motornet_backfill_pick_version', 'Seleziona versione')}
							</div>
							<Select<VersionOption>
								options={versionOptions}
								value={selectedVersion}
								isClearable
								isDisabled={isSaving}
								placeholder={t(
									'motornet_import_pick_version',
									'seleziona versione…'
								)}
								menuPortalTarget={document.body}
								styles={{
									menuPortal: (base) => ({ ...base, zIndex: 1090 }),
								}}
								noOptionsMessage={() =>
									matchesFailed
										? t(
											'motornet_backfill_matches_error',
											'Errore nel recupero delle versioni'
										)
										: t('eurotax_vehicleSelect_noResults', 'Nessun risultato')
								}
								onChange={(option) => setSelectedKey(option?.value ?? null)}
							/>
						</>
					))}
				{isNotFound && (
					<>
						<div className="font-weight-bold mb-2">
							{t('motornet_backfill_pick_version', 'Seleziona versione')}
						</div>
						<Row>
							<MotornetVehicleSelector
								selectedVehicle={{
									brand: manualBrand,
									model: manualModel,
									version: manualVersion,
								}}
								onBrandChange={setManualBrand}
								onModelChange={setManualModel}
								onVersionChange={setManualVersion}
								showSelectLabels
								requiredBrand
								requiredModel
								requiredVersion
								disabled={isSaving}
								vertical
							/>
						</Row>
					</>
				)}
			</Modal.Body>
			<Modal.Footer>
				<Button
					onClick={handleSave}
					disabled={
						!resolveBody || isSaving || (isAmbiguous && isLoadingMatches)
					}
				>
					{(isSaving || (isNotFound && isHydratingVersion)) && (
						<Spinner animation="border" size="sm" className="me-2" />
					)}
					{t('motornet_import_save', 'Salva')}
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

export default function ResolveVehiclePage() {
	const { t } = useTranslation();
	const { apiClient } = useApiClient();
	const queryClient = useQueryClient();
	const [searchParams, setSearchParams] = useSearchParams();

	const filter = parseFilter(searchParams.get('filter'));
	const manufactureCode = searchParams.get('code') ?? '';
	const currentPage = parsePage(searchParams.get('page'));
	const pageSize = parsePageSize(searchParams.get('pageSize'));

	const [manufactureCodeInput, setManufactureCodeInput] =
		useState(manufactureCode);
	const [selectedVehicle, setSelectedVehicle] =
		useState<EurotaxBackfillVehicle | null>(null);
	const { data, isPending, isFetching, error } = useBackfillVehicles(
		currentPage,
		pageSize,
		filter,
		manufactureCode
	);

	useEffect(() => {
		setManufactureCodeInput(manufactureCode);
	}, [manufactureCode]);

	const updateParams = useCallback(
		(patch: Record<string, string | null>) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					Object.entries(patch).forEach(([key, value]) =>
						value === null ? next.delete(key) : next.set(key, value)
					);
					return next;
				},
				{ replace: true }
			);
		},
		[setSearchParams]
	);

	const goToPage = useCallback(
		(page: number) => updateParams({ page: String(page) }),
		[updateParams]
	);

	useEffect(() => {
		const seed: Record<string, string> = {};
		if (!searchParams.has('filter')) seed.filter = DEFAULT_FILTER;
		if (!searchParams.has('page')) seed.page = '1';
		if (Object.keys(seed).length > 0) updateParams(seed);
	}, [searchParams, updateParams]);

	const handleFilterChange = (nextFilter: EurotaxBackfillFilter | '') => {
		updateParams({ filter: serializeFilter(nextFilter), page: '1' });
	};

	const handleManufactureCodeSearch = () => {
		updateParams({ code: manufactureCodeInput.trim() || null, page: '1' });
	};

	const handleManufactureCodeReset = () => {
		setManufactureCodeInput('');
		updateParams({ code: null, page: '1' });
	};

	const vehicles = useMemo(() => data?.vehicles ?? [], [data]);
	const lastKnownTotal = useRef(0);

	if (data) {
		lastKnownTotal.current = data.total;
	}
	const totalCount = data?.total ?? lastKnownTotal.current;

	useEffect(() => {
		if (
			data &&
			data.total > 0 &&
			data.vehicles.length === 0 &&
			currentPage > 1
		) {
			goToPage(1);
		}
	}, [data, currentPage, goToPage]);

	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [bulkOpen, setBulkOpen] = useState(false);

	const bulkEligible =
		manufactureCode !== '' &&
		(filter === 'ambiguous' || filter === 'notFound') &&
		vehicles.length > 0 &&
		vehicles.every((vehicle) => vehicle.codice_costruttore === manufactureCode);

	useEffect(() => {
		setSelectedIds(new Set());
		setBulkOpen(false);
	}, [manufactureCode, filter]);

	const toggleOne = useCallback((id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	}, []);

	const toggleAll = useCallback(() => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			const pageIds = vehicles.map((vehicle) => vehicle.platform_uuid);
			const everySelected =
				pageIds.length > 0 && pageIds.every((id) => next.has(id));
			pageIds.forEach((id) => (everySelected ? next.delete(id) : next.add(id)));
			return next;
		});
	}, [vehicles]);

	const allSelected =
		bulkEligible &&
		vehicles.every((vehicle) => selectedIds.has(vehicle.platform_uuid));
	const someSelected =
		bulkEligible &&
		vehicles.some((vehicle) => selectedIds.has(vehicle.platform_uuid));

	const selectColumn = useMemo(
		() => ({
			id: 'select',
			disableSortBy: true,
			width: 44,
			Header: () => (
				<SelectAllCheckbox
					checked={allSelected}
					indeterminate={someSelected}
					onChange={toggleAll}
					ariaLabel={t('motornet_backfill_select_all', 'Seleziona tutti')}
				/>
			),
			Cell: ({ row }: any) => (
				<input
					type="checkbox"
					className="form-check-input"
					checked={selectedIds.has(row.original.platform_uuid)}
					onChange={() => toggleOne(row.original.platform_uuid)}
					onClick={(event) => event.stopPropagation()}
					aria-label={t('motornet_backfill_select_row', 'Seleziona veicolo')}
				/>
			),
		}),
		[allSelected, someSelected, selectedIds, toggleAll, toggleOne, t]
	);

	const retryMutation = useMutation({
		mutationFn: (mongoId: string) =>
			apiClient.motornetControllerService.retryEurotaxBackfill(mongoId),
		onSuccess: (result) => {
			const outcomeLabels: Record<EurotaxBackfillRetryOutcome, string> = {
				resolved: t('motornet_backfill_resolved', 'Risolto'),
				ambiguous: t('motornet_backfill_ambiguous', 'Ambiguo'),
				notFound: t('motornet_backfill_not_found', 'Non trovato'),
				priceMismatch: t(
					'motornet_backfill_filter_price_mismatch',
					'Prezzo divergente'
				),
				skipped: t('motornet_backfill_filter_skipped', 'Saltato'),
			};
			const outcome = outcomeLabels[result.outcome] ?? result.outcome;
			const message = t('motornet_backfill_retry_done', 'Retry: {{outcome}}', {
				outcome,
			});
			if (result.outcome === 'resolved') {
				toaster.success(message);
			} else {
				toaster.info(message);
			}
			void queryClient.invalidateQueries({
				queryKey: ['motornet', 'eurotax-backfill'],
			});
		},
		onError: (error) => {
			toaster.error(
				error instanceof Error && error.message
					? error.message
					: t('motornet_backfill_retry_error', 'Retry non riuscito')
			);
		},
	});

	const columns = useMemo(
		() => [
			{
				id: 'vehicle',
				Header: t('motornet_backfill_vehicle', 'Veicolo'),
				accessor: (vehicle: EurotaxBackfillVehicle) =>
					`${vehicle.marca} ${vehicle.modello}`,
				disableSortBy: true,
				Cell: ({ row }: any) => (
					<>
						<div className="font-weight-bold">
							{row.original.marca} {row.original.modello}
						</div>
						<small className="text-muted">{row.original.allestimento}</small>
					</>
				),
			},
			{
				id: 'platform_uuid',
				Header: t('motornet_backfill_platform_id', 'Platform id'),
				accessor: 'platform_uuid',
				disableSortBy: true,
				Cell: ({ row }: any) => (
					<span className="d-inline-flex align-items-center">
						<Link
							to={`/details/${row.original.platform_uuid}`}
							target="_blank"
							rel="noopener noreferrer"
						>
							{row.original.platform_uuid}
						</Link>
						<CopyIdButton
							value={row.original.platform_uuid}
							label={t('motornet_backfill_platform_id', 'Platform id')}
						/>
					</span>
				),
			},
			{
				id: 'codice_costruttore',
				Header: t(
					'motornet_backfill_manufacturer_code_jato',
					'Cod. costruttore Jato'
				),
				accessor: 'codice_costruttore',
				disableSortBy: true,
				Cell: ({ row }: any) => (
					<span className="d-inline-flex align-items-center">
						{row.original.codice_costruttore}
						{row.original.codice_costruttore && (
							<CopyIdButton
								value={row.original.codice_costruttore}
								label={t(
									'motornet_backfill_manufacturer_code_jato',
									'Cod. costruttore Jato'
								)}
							/>
						)}
					</span>
				),
			},
			{
				id: 'manufacture_code',
				Header: t(
					'motornet_backfill_manufacturer_code_motornet',
					'Cod. costruttore Motornet'
				),
				accessor: 'manufacture_code',
				disableSortBy: true,
				Cell: ({ row }: any) => (
					<span className="d-inline-flex align-items-center">
						{row.original.manufacture_code}
						{row.original.manufacture_code && (
							<CopyIdButton
								value={row.original.manufacture_code}
								label={t(
									'motornet_backfill_manufacturer_code_motornet',
									'Cod. costruttore Motornet'
								)}
							/>
						)}
					</span>
				),
			},
			{
				id: 'jato_id',
				Header: t('motornet_backfill_jato_id', 'JATO id'),
				accessor: 'jato_id',
				disableSortBy: true,
				Cell: ({ row }: any) => (
					<span className="d-inline-flex align-items-center">
						{row.original.jato_id != null && (
							<>
								{row.original.jato_id}
								<CopyIdButton
									value={String(row.original.jato_id)}
									label={t('motornet_backfill_jato_id', 'JATO id')}
								/>
							</>
						)}
					</span>
				),
			},
			{
				id: 'status',
				Header: t('motornet_backfill_status', 'Stato'),
				accessor: statusKeyOf,
				disableSortBy: true,
				Cell: ({ row }: any) => <StatusBadge vehicle={row.original} />,
			},
			{
				id: 'actions',
				Header: '',
				disableSortBy: true,
				headerClassName: 'column-sticky',
				className: 'column-sticky',
				width: 96,
				Cell: ({ row }: any) => {
					const vehicle = row.original as EurotaxBackfillVehicle;
					const isViewOnly = !isResolvable(vehicle);
					const actionLabel = isViewOnly
						? t('motornet_backfill_view_action', 'Visualizza')
						: t('motornet_backfill_resolve_action', 'Risolvi');
					const retryLabel = t('motornet_backfill_retry_action', 'Riprova');
					const isRetrying =
						retryMutation.isPending &&
						retryMutation.variables === vehicle.platform_uuid;

					return (
						<div className="d-inline-flex align-items-center gap-2">
							{isRetryable(vehicle) && (
								<Button
									variant="link"
									className="p-0"
									aria-label={retryLabel}
									title={retryLabel}
									disabled={isRetrying}
									onClick={() => retryMutation.mutate(vehicle.platform_uuid)}
								>
									{isRetrying ? (
										<Spinner animation="border" size="sm" />
									) : (
										<FaRedo size={18} />
									)}
								</Button>
							)}
							<Button
								variant="link"
								className="p-0"
								aria-label={actionLabel}
								title={actionLabel}
								onClick={() => setSelectedVehicle(vehicle)}
							>
								{isViewOnly ? <FaEye size={20} /> : <FaRegEdit size={20} />}
							</Button>
						</div>
					);
				},
			},
		],
		[t, retryMutation.isPending, retryMutation.variables, retryMutation.mutate]
	);

	const tableColumns = useMemo(
		() => (bulkEligible ? [selectColumn, ...columns] : columns),
		[bulkEligible, selectColumn, columns]
	);

	const [exportProgress, setExportProgress] = useState<{
		loaded: number;
		total: number;
	} | null>(null);
	const isExporting = exportProgress !== null;

	const canExport =
		!!manufactureCode || (filter !== '' && filter !== 'resolved');

	const fetchExportPage = useCallback(
		(page: number) =>
			apiClient.motornetControllerService.getEurotaxBackfillVehicles({
				page,
				pageSize: EXPORT_BATCH_SIZE,
				filter: filter || undefined,
				manufactureCode: manufactureCode || undefined,
			}),
		[apiClient, filter, manufactureCode]
	);

	const fetchAllForExport = useCallback(async () => {
		const firstPage = await fetchExportPage(1);
		const all = [...firstPage.vehicles];
		const pageCount = Math.ceil(firstPage.total / EXPORT_BATCH_SIZE);

		setExportProgress({ loaded: all.length, total: firstPage.total });

		for (let page = 2; page <= pageCount; page += 1) {
			const { vehicles } = await fetchExportPage(page);
			if (vehicles.length === 0) break;
			all.push(...vehicles);
			setExportProgress({ loaded: all.length, total: firstPage.total });
		}

		return all;
	}, [fetchExportPage]);

	const handleExport = useCallback(
		async (format: ExportFormat) => {
			setExportProgress({ loaded: 0, total: totalCount });
			try {
				const exportData = buildExportData(await fetchAllForExport());
				const baseName = "Ris veicoli".replace(/ /g, '_');
				const fileName = `${baseName}_${exportTimestamp()}`;

				if (format === 'xlsx') {
					downloadXLSX(exportData, fileName);
				} else {
					downloadCSV(exportData, fileName);
				}
			} catch (error) {
				toaster.error(
					error instanceof Error && error.message
						? error.message
						: t('error_fetching_data')
				);
			} finally {
				setExportProgress(null);
			}
		},
		[fetchAllForExport, totalCount, t]
	);

	const exportLabel = exportProgress ? (
		<>
			<Spinner animation="border" size="sm" className="me-2" />
			{exportProgress.loaded.toLocaleString('it-IT')} /{' '}
			{exportProgress.total.toLocaleString('it-IT')}
		</>
	) : undefined;

	const exportHint = t(
		'motornet_backfill_export_hint',
		'Filtra per stato o cerca un codice costruttore per esportare'
	);

	return (
		<Container fluid className="resolve-vehicle-page">
			<div className="d-flex align-items-center justify-content-between mb-3">
				<div className="display-6">
					{t('motornet_backfill_title', 'Risoluzione veicoli')}
				</div>
				{!error && totalCount > 0 && (
					<span
						className="export-menu-wrapper"
						title={canExport ? undefined : exportHint}
					>
						<FileExportMenu
							isDisabled={!canExport || isExporting || isPending}
							allowedFormats={['csv', 'xlsx']}
							handleExport={handleExport}
							label={exportLabel}
						/>
					</span>
				)}
			</div>
			<Card>
				<Card.Body className="py-3 filter-bar">
					<div className="d-flex justify-content-start">
						<Form.Select
							className="w-auto"
							aria-label={t(
								'motornet_backfill_filter_label',
								'Filtra per stato'
							)}
							value={filter}
							onChange={(event) =>
								handleFilterChange(
									event.target.value as EurotaxBackfillFilter | ''
								)
							}
						>
							<option value="">
								{t('motornet_backfill_filter_all', 'Tutti gli stati')}
							</option>
							{FILTER_OPTIONS.map((option) => (
								<option key={option.value} value={option.value}>
									{t(option.labelKey, option.fallback)}
								</option>
							))}
						</Form.Select>
					</div>
					<Form
						className="d-flex align-items-center"
						onSubmit={(event) => {
							event.preventDefault();
							handleManufactureCodeSearch();
						}}
					>
						<Form.Control
							className="flex-grow-1"
							type="search"
							value={manufactureCodeInput}
							placeholder={t(
								'motornet_backfill_search_manufacture_code',
								'Cod. costruttore Jato…'
							)}
							aria-label={t(
								'motornet_backfill_manufacturer_code_jato',
								'Cod. costruttore Jato'
							)}
							onChange={(event) => {
								const value = event.target.value;
								setManufactureCodeInput(value);
								if (value === '') {
									updateParams({ code: null, page: '1' });
								}
							}}
						/>
					</Form>
					<div className="d-flex align-items-center justify-content-end gap-2">
						{(manufactureCodeInput !== '' || manufactureCode !== '') && (
							<Button
								type="button"
								className="filter-reset-btn text-nowrap"
								onClick={handleManufactureCodeReset}
							>
								{t('reset_filters')}
							</Button>
						)}
						<Button
							type="button"
							variant="outline-secondary"
							className="text-nowrap"
							onClick={handleManufactureCodeSearch}
						>
							{t('motornet_backfill_search', 'Cerca')}
						</Button>
						{bulkEligible && (
							<Button
								variant="primary"
								className="text-nowrap"
								disabled={selectedIds.size === 0}
								onClick={() => setBulkOpen(true)}
							>
								{t('motornet_backfill_bulk_action', 'Bulk selezionati')}
								{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
							</Button>
						)}
					</div>
				</Card.Body>
			</Card>
			{!error && (
				<div className="table-results-container">
					{t('show')}:
					<span className="table-result-count ms-2">{totalCount}</span>
				</div>
			)}
			{error ? (
				<Card className="my-4">
					<Card.Body className="text-center text-danger py-5">
						{error instanceof Error && error.message
							? error.message
							: t('error_loading_data')}
					</Card.Body>
				</Card>
			) : !isPending && !isFetching && totalCount === 0 ? (
				<Card className="my-4">
					<Card.Body className="text-center text-muted py-5">
						{t('motornet_backfill_no_results', 'Nessun risultato presente')}
					</Card.Body>
				</Card>
			) : (
				<ServerPaginatedTable
					key={`${filter}-${manufactureCode}-${pageSize}`}
					columns={tableColumns}
					data={vehicles}
					loading={isPending || isFetching}
					error={error}
					totalCount={totalCount}
					pageSize={pageSize}
					showPageSelector
					pageSizeOptions={PAGE_SIZE_OPTIONS}
					initialPage={currentPage}
					onPageSizeChange={(newSize: number) =>
						updateParams({ pageSize: String(newSize), page: '1' })
					}
					loadFn={(skip: number, limit: number) =>
						goToPage(Math.floor(skip / limit) + 1)
					}
					dblClickCallback={(vehicle: EurotaxBackfillVehicle) =>
						setSelectedVehicle(vehicle)
					}
				/>
			)}
			<ResolveVehicleModal
				vehicle={selectedVehicle}
				onClose={() => setSelectedVehicle(null)}
			/>
			<BulkResolveModal
				open={bulkOpen}
				mode={filter === 'notFound' ? 'notFound' : 'ambiguous'}
				manufactureCode={manufactureCode}
				mongoIds={Array.from(selectedIds)}
				onClose={() => setBulkOpen(false)}
				onResolved={() => setSelectedIds(new Set())}
			/>
		</Container>
	);
}
